import {
  editBookingOnLoad,
  requestSecSession,
  searchBooking,
  searchBookingEnc,
} from "api/services/Form";
import Layout from "components/Layout";
import { useError } from "context/ErrorContext";
import { useSecSession } from "context/SecSessionContext";
import {
  Booking,
  EditBookingOnLoadRes,
  RequestSecSessionRes,
  SearchBookingReqConfig,
  SearchBookingResNoOtp,
  SearchBookingResRequireOtp,
} from "entities/Form";
import { AppointmentResult, RESULT } from "entities/Result";
import Disable from "pages/Disable";
import ErrorBoundary from "pages/ErrorBoundary/ErrorBoundary";
import Result from "pages/Result";
import Validation from "pages/Validation";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  constructSecurePayload,
  generateRandomString,
  getApiLangParam,
  getLocalizedValue,
  verifySignatureWithPKCS1,
} from "utils/helper";
import { EDIT_PAGE } from "../../../entities";
import BookingDetail from "../BookingDetail";
import Form from "../Form";
import { resolveCreateBookingPath } from "./routing";

const defaultValues = {
  countryCode: "852",
  mobileNo: "",
  bookingNo: "",
};

interface EditLocationState {
  createBookingPath?: string;
}

const EditAppointment: React.FC = () => {
  const { i18n } = useTranslation();
  const { pathname, state } = useLocation();
  const navigate = useNavigate();
  const { setError } = useError();
  const [searchParams] = useSearchParams();
  const { secSession, setSecSession } = useSecSession();
  const cmsSiteId =
    searchParams.get("site") || searchParams.get("cmsSiteId") || "";
  const requestedCreateBookingPath = (state as EditLocationState | null)
    ?.createBookingPath;
  const createBookingPath = resolveCreateBookingPath(
    pathname,
    requestedCreateBookingPath,
  );
  const createBookingUrl = cmsSiteId
    ? `${createBookingPath}?site=${cmsSiteId}`
    : createBookingPath;
  const prevCmsSiteIdRef = useRef<string | null>(null);
  const [editBookingOnLoadData, setEditBookingOnLoadData] =
    useState<EditBookingOnLoadRes>();
  const systemMessage = editBookingOnLoadData?.systemMessage?.trim();
  const [bookingOnLoadDataError, setBookingOnLoadDataError] = useState(false);
  const [page, setPage] = useState(EDIT_PAGE.FORM);
  const [formValues, setFormValues] = useState(defaultValues);
  const [isFormValuesDirty, setIsFormValuesDirty] = useState(false);
  const [otpSid, setOtpSid] = useState<string>();
  const [booking, setBooking] = useState<Booking>();
  const [notes, setNotes] = useState<string>();
  const [enquiryPhoneNo, setEnquiryPhoneNo] = useState<string>();
  const [isEdit, setIsEdit] = useState(false);
  const [isCancel, setIsCancel] = useState(false);
  const [result, setResult] = useState<AppointmentResult>();

  const init = () => {
    setBooking(undefined);
    setNotes(undefined);
    setEnquiryPhoneNo(undefined);
    setIsEdit(false);
    setIsCancel(false);
    setFormValues(defaultValues);
    setIsFormValuesDirty(false);
    setResult(undefined);
    setOtpSid(undefined);
    setPage(EDIT_PAGE.FORM);
  };

  const onSearchBookingSuccess = (
    res: SearchBookingResNoOtp | SearchBookingResRequireOtp
  ) => {
    if (res.isRequireOtp) setPage(EDIT_PAGE.VALIDATION);
    else if (res.error) {
      setPage(EDIT_PAGE.RESULT);
      setResult({
        status: RESULT.ERROR,
        enquiryPhoneNo: res.enquiryPhoneNo,
        error: res.error,
      });
    } else {
      setPage(EDIT_PAGE.BOOKING);
      setBooking(res.booking);
      setNotes(res.notes);
      setEnquiryPhoneNo(res.enquiryPhoneNo);
      setIsEdit(res?.isEdit ?? false);
      setIsCancel(res?.isCancel ?? false);
    }
  };

  const submitSearchBooking = (otpSid?: string, onError?: () => void) => {
    const { countryCode, mobileNo, bookingNo } = formValues;
    const payload = {
      countryCode,
      mobileNo,
      bookingNo,
      otpSid,
    } as SearchBookingReqConfig;

    if (editBookingOnLoadData?.enc) {
      const onSearchBookingEnc = async (secSession: RequestSecSessionRes) =>
        searchBookingEnc(
          await constructSecurePayload(
            { lang: getApiLangParam(i18n.language), ...payload },
            secSession.pk
          ),
          (res) => {
            onSearchBookingSuccess(res);
          },
          onError
        );

      const onSecSession = async (secSession: RequestSecSessionRes) => {
        if (secSession.sign && process.env.REACT_APP_SERVER_PUBLIC_CERT) {
          const isValid = await verifySignatureWithPKCS1(
            process.env.REACT_APP_SERVER_PUBLIC_CERT,
            secSession.pk,
            secSession.sign
          );
          if (!isValid) {
            setError({
              message: i18n.t("securityWarningMessage"),
              confirmTitle: i18n.t("reload"),
              onConfirm: () => navigate(0),
            });
            console.error("Signature is invalid!");
            return;
          }
        }
        onSearchBookingEnc(secSession);
      };

      !secSession
        ? requestSecSession(
            { k: generateRandomString() },
            async (secSession) => {
              setSecSession(secSession);
              onSecSession(secSession);
            },
            () => setSecSession(null)
          )
        : onSearchBookingEnc(secSession);
    } else {
      setSecSession(null);
      searchBooking(
        payload,
        (res) => {
          onSearchBookingSuccess(res);
        },
        onError
      );
    }
  };

  useEffect(() => {
    if (
      cmsSiteId !== null &&
      cmsSiteId !== undefined &&
      cmsSiteId !== prevCmsSiteIdRef.current
    ) {
      prevCmsSiteIdRef.current = cmsSiteId;
      editBookingOnLoad({}, setEditBookingOnLoadData, () => {
        setBookingOnLoadDataError(true);
      });
    }
  }, [cmsSiteId]);

  return (
    <Layout isFormValuesDirty={isFormValuesDirty}>
      {systemMessage ? (
        <Disable systemMessage={systemMessage} />
      ) : bookingOnLoadDataError ? (
        <ErrorBoundary />
      ) : (
        <>
          {page === EDIT_PAGE.FORM && (
            <Form
              loading={!editBookingOnLoadData}
              createBookingUrl={createBookingUrl}
              setIsFormValuesDirty={setIsFormValuesDirty}
              formValues={formValues}
              onChangeFormValues={setFormValues}
              submit={submitSearchBooking}
            />
          )}
          {page === EDIT_PAGE.VALIDATION && (
            <Validation
              type="edit"
              onSubmit={(otpSid) => {
                setOtpSid(otpSid);
                submitSearchBooking(otpSid);
              }}
              onBack={() => {
                setPage(EDIT_PAGE.FORM);
              }}
              mobileNo={formValues.mobileNo}
              countryCode={formValues.countryCode}
            />
          )}
          {page === EDIT_PAGE.BOOKING && booking && editBookingOnLoadData && (
            <BookingDetail
              otpSid={otpSid}
              booking={booking}
              notes={notes}
              enquiryPhoneNo={enquiryPhoneNo}
              isEdit={isEdit}
              isCancel={isCancel}
              onBackToHomePage={() => {
                setBooking(undefined);
                setFormValues(defaultValues);
                setIsFormValuesDirty(false);
                setResult(undefined);
                setOtpSid(undefined);
                setPage(EDIT_PAGE.FORM);
              }}
              onSuccessCancelBooking={(res) => {
                const { enquiryPhoneNo, bookingNo } = res;
                const locationName = getLocalizedValue({
                  nameSc: res.locationNameSc || "",
                  nameTc: res.locationNameTc || "",
                  name: res.locationName || "",
                });

                const bookingTime = getLocalizedValue({
                  nameSc: res.bookingTimeLabelSc || "",
                  nameTc: res.bookingTimeLabelTc || "",
                  name: res.bookingTimeLabel || "",
                });
                setResult({
                  status: RESULT.CANCELLED,
                  enquiryPhoneNo,
                  bookingNo: bookingNo ?? "",
                  locationName,
                  bookingTime,
                });
                setPage(EDIT_PAGE.RESULT);
              }}
              onSuccessEditBooking={(res) => {
                const { enquiryPhoneNo, bookingNo } = res;
                const locationName = getLocalizedValue({
                  nameSc: res.locationNameSc || "",
                  nameTc: res.locationNameTc || "",
                  name: res.locationName || "",
                });

                const bookingTime = getLocalizedValue({
                  nameSc: res.bookingTimeLabelSc || "",
                  nameTc: res.bookingTimeLabelTc || "",
                  name: res.bookingTimeLabel || "",
                });

                setResult({
                  status: res.isConfirmed ? RESULT.SUCCESS : RESULT.PENDING,
                  enquiryPhoneNo: enquiryPhoneNo || "",
                  bookingNo,
                  locationName,
                  bookingTime,
                });
                setPage(EDIT_PAGE.RESULT);
              }}
            />
          )}
          {page === EDIT_PAGE.RESULT && result && (
            <Result
              result={result}
              onReStart={() => navigate(createBookingUrl)}
              onEdit={() => setPage(EDIT_PAGE.FORM)}
              onSearchAnotherAppointment={init}
            />
          )}
        </>
      )}
    </Layout>
  );
};

export default EditAppointment;
